import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { showError } from '../utility/common-logic';

@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly destroyRef = inject(DestroyRef);
  private updateCheckIntervalId?: ReturnType<typeof setInterval>;
  private initialized: boolean = false;

  constructor(
    private swUpdate: SwUpdate,
    private toastr: ToastrService,
    private translate: TranslateService,
  ) {
    this.destroyRef.onDestroy(() => {
      if (this.updateCheckIntervalId) {
        clearInterval(this.updateCheckIntervalId);
        this.updateCheckIntervalId = undefined;
      }
      document.removeEventListener(
        'visibilitychange',
        this.handleVisibilityChange,
      );
      this.initialized = false;
    });
  }

  async init() {
    if (this.initialized) return;
    if (!this.swUpdate.isEnabled) return;
    this.initialized = true;

    try {
      await this.swUpdate.checkForUpdate();
    } catch (err) {
      showError(err, this.toastr, this.translate);
    }

    this.swUpdate.versionUpdates
      .pipe(
        filter(
          (event): event is VersionReadyEvent => event.type === 'VERSION_READY',
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        void this.activateUpdate().catch((err) => {
          showError(err, this.toastr, this.translate);
        });
      });

    if (this.updateCheckIntervalId) {
      clearInterval(this.updateCheckIntervalId);
    }
    this.updateCheckIntervalId = setInterval(
      () =>
        void this.swUpdate.checkForUpdate().catch((err) => {
          showError(err, this.toastr, this.translate, true);
        }),
      5 * 60 * 1000,
    );

    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange,
    );
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  private readonly handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      void this.swUpdate.checkForUpdate().catch((err) => {
        showError(err, this.toastr, this.translate, true);
      });
    }
  };

  private async activateUpdate() {
    await this.swUpdate.activateUpdate();
    window.location.reload();
  }
}
